import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { filter, fromEvent, Subject, takeUntil } from 'rxjs';

@Directive({
  selector: '[luiKeyboardFocus]',
  standalone: true,
})
export class KeyboardFocusDirective implements AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() autoFocus = false;
  @Input() autoSelectOnFocus = false;
  @Input() autoBlurOnEscape = true;
  @Input() preventDefaultOnKeys = true;

  @Output() escapePressed = new EventEmitter<void>();
  @Output() enterPressed = new EventEmitter<void>();
  @Output() tabPressed = new EventEmitter<void>();
  @Output() arrowUpPressed = new EventEmitter<void>();
  @Output() arrowDownPressed = new EventEmitter<void>();
  @Output() focusChanged = new EventEmitter<boolean>();

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngAfterViewInit() {
    if (this.autoFocus) {
      this.focus();

      if (this.autoSelectOnFocus) {
        window.getSelection()?.selectAllChildren(this.elementRef.nativeElement);
      }
    }

    this.setupKeyboardListeners();
    this.setupFocusListeners();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  focus() {
    this.elementRef.nativeElement.focus();
  }

  blur() {
    this.elementRef.nativeElement.blur();
  }

  private setupKeyboardListeners() {
    const keydown$ = fromEvent<KeyboardEvent>(
      this.elementRef.nativeElement,
      'keydown'
    );

    // Escape handler
    keydown$
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event.key === 'Escape')
      )
      .subscribe((event) => {
        if (this.preventDefaultOnKeys) {
          event.preventDefault();
        }
        if (this.autoBlurOnEscape) {
          this.blur();
        }
        this.escapePressed.emit();
      });

    // Enter handler
    keydown$
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event.key === 'Enter')
      )
      .subscribe((event) => {
        if (this.preventDefaultOnKeys) {
          event.preventDefault();
        }
        this.enterPressed.emit();
      });

    // Tab handler
    keydown$
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event.key === 'Tab')
      )
      .subscribe((event) => {
        if (this.preventDefaultOnKeys) {
          event.preventDefault();
        }
        this.tabPressed.emit();
      });

    // Arrow Up handler
    keydown$
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event.key === 'ArrowUp')
      )
      .subscribe((event) => {
        if (this.preventDefaultOnKeys) {
          event.preventDefault();
        }
        this.arrowUpPressed.emit();
      });

    // Arrow Down handler
    keydown$
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event.key === 'ArrowDown')
      )
      .subscribe((event) => {
        if (this.preventDefaultOnKeys) {
          event.preventDefault();
        }
        this.arrowDownPressed.emit();
      });
  }

  private setupFocusListeners() {
    // Focus events
    fromEvent(this.elementRef.nativeElement, 'focus')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.focusChanged.emit(true);
      });

    fromEvent(this.elementRef.nativeElement, 'blur')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.focusChanged.emit(false);
      });
  }
}
